import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PublicProfileAnalyticsSummary,
  PublicProfileAnalyticsRange,
  PublicProfileAnalyticsTotals,
  PublicProfileAnalyticsBreakdown,
  PublicProfileMode,
  PublicProfileSocial,
  PublicProfileTeamMember,
  PublicProfileContact,
  WorkspaceSummary,
  PublicProfileBuilderState,
  PublicProfileEffectivePrivacy,
} from '@tabbio/types';
import {
  DEFAULT_PUBLIC_PROFILE_PRIVACY,
  getPublicProfileEffectivePrivacy,
  normalizeProfilePrivacySettings,
} from '@tabbio/utils/profile-privacy';
import type {
  ProfileFeaturedProject,
  ProfileResponse,
  ProfileServiceOffer,
} from '../lib/profile-api';
import {
  clonePublicProfileBlocks,
  createNewBlock,
  getPublicProfileTheme,
  isBuiltInContactChannel,
  mapCompanyChecklistToItems,
  PUBLIC_PROFILE_ACCENTS,
  PUBLIC_PROFILE_CHECKLIST_ORDER,
  PUBLIC_PROFILE_EXPLORE_TILES_INDIVIDUAL,
  PUBLIC_PROFILE_SUGGESTED_BLOCKS_INDIVIDUAL,
  type PublicProfileAnyChecklistId,
  type PublicProfileBlock,
  type PublicProfileButtonShape,
  type PublicProfileButtonStyle,
  type PublicProfileChecklistId,
  type PublicProfileCompanyChecklist,
  type PublicProfileExploreTile,
  type PublicProfileSharePreviewBackground,
  type PublicProfileSuggestedBlock,
  type PublicProfileTheme,
} from '../lib/public-profile-builder';
import { serializeBlocks } from '../lib/public-profile-serialize';
import { QUERY_KEYS } from '../lib/query-keys';

import {
  confirmPublicProfileShare,
  fetchPublicProfileBuilderState,
  updatePublicProfileBlocks,
  updatePublicProfileContacts,
  updatePublicProfileDesign,
  updatePublicProfileSettings,
  updatePublicProfileSocials,
  updatePublicProfileTeam,
} from '../lib/public-profiles-api';
import { buildPublicProfileUrl } from '../lib/public-web';

type BuilderUser = {
  id: string;
  name: string;
  email?: string | null;
  image?: string | null;
  username?: string | null;
  phone?: string | null;
  locale?: string | null;
  role?: string | null;
};

// Stable empty override set — module-level so the draft memo does not churn
// for a scope the user has not edited yet.
const NO_DRAFT_OVERRIDES: Partial<DraftProfile> = {};

// Stable empty catalogues for company mode — module-level so downstream memos
// keyed on these values do not churn every render.
const NO_SUGGESTED_BLOCKS: PublicProfileSuggestedBlock[] = [];
const NO_EXPLORE_TILES: PublicProfileExploreTile[] = [];

type DraftProfile = {
  name: string;
  bio: string;
  location: string;
  website: string;
  /**
   * Company one-line bio (PRD §6.1) — distinct from the long description that
   * `bio` carries in company mode. Individual mode leaves it empty ('' —
   * bio/headline stay coupled there).
   */
  tagline: string;
};

// Server checklist rides on the company builder GET additively; the shared
// PublicProfileBuilderState contract is unchanged, so read it tolerantly.
type BuilderStateWithChecklist = PublicProfileBuilderState & {
  checklist?: PublicProfileCompanyChecklist | null;
};

type BuilderMode = PublicProfileMode;

type BuilderScope = {
  profile?: ProfileResponse;
  user?: BuilderUser | null;
  workspace?: WorkspaceSummary;
};

interface ScopedBuilderOverrides {
  designOverrides: {
    accent: boolean;
    buttonStyle: boolean;
    buttonShape: boolean;
  };
}

export interface PublicProfileChecklistItem {
  id: PublicProfileAnyChecklistId;
  completed: boolean;
}

export interface PublicProfileBuilderModel {
  pageId: string | null;
  isHydrated: boolean;
  isLoading: boolean;
  loadError: string | null;
  isSaving: boolean;
  saveError: string | null;
  retry: () => Promise<unknown>;
  clearSaveError: () => void;
  isPreview: boolean;
  setIsPreview: (value: boolean) => void;
  profileMode: BuilderMode;
  setProfileMode: (value: BuilderMode) => void;
  routeMode: BuilderMode;
  draftProfile: DraftProfile;
  setDraftProfile: (patch: Partial<DraftProfile>) => void;
  username: string;
  avatarUrl: string;
  publicPrivacy: PublicProfileEffectivePrivacy;
  blocks: PublicProfileBlock[];
  setBlocks: React.Dispatch<React.SetStateAction<PublicProfileBlock[]>>;
  toggleBlockEnabled: (id: string) => void;
  reorderBlock: (id: string, direction: 'up' | 'down') => void;
  reorderBlocksByIndices: (fromIndex: number, toIndex: number) => void;
  addBlock: (type: PublicProfileBlock['type']) => PublicProfileBlock;
  socials: PublicProfileSocial[];
  activeSocials: PublicProfileSocial[];
  toggleSocial: (id: PublicProfileSocial['id']) => void;
  updateSocialUrl: (id: PublicProfileSocial['id'], url: string) => void;
  contacts: PublicProfileContact[];
  activeContacts: PublicProfileContact[];
  toggleContact: (id: PublicProfileContact['id']) => void;
  updateContactValue: (id: PublicProfileContact['id'], value: string) => void;
  theme: PublicProfileTheme['id'];
  setTheme: (value: PublicProfileTheme['id']) => void;
  accent: string;
  setAccent: (value: string) => void;
  buttonStyle: PublicProfileButtonStyle;
  setButtonStyle: (value: PublicProfileButtonStyle) => void;
  buttonShape: PublicProfileButtonShape;
  setButtonShape: (value: PublicProfileButtonShape) => void;
  sharePreviewBackground: PublicProfileSharePreviewBackground;
  setSharePreviewBackground: (value: PublicProfileSharePreviewBackground) => void;
  hideBranding: boolean;
  setHideBranding: (value: boolean) => void;
  sensitiveContent: boolean;
  setSensitiveContent: (value: boolean) => void;
  showContactChannelsPublicly: boolean;
  setShowContactChannelsPublicly: (value: boolean) => void;
  hasSharedTabbio: boolean;
  setHasSharedTabbio: (value: boolean) => void;
  /**
   * Company mode: POSTs builder/share-confirm after a successful share action
   * (copy / QR download / native share) so the server stamps the persisted
   * `shared` checklist outcome. Fire-and-forget — silent on failure. No-op in
   * individual mode.
   */
  confirmCompanyShare: () => void;
  teamMembers: PublicProfileTeamMember[];
  setTeamMembers: React.Dispatch<React.SetStateAction<PublicProfileTeamMember[]>>;
  checklistItems: PublicProfileChecklistItem[];
  checklistDone: number;
  checklistTotal: number;
  completionPercent: number;
  suggestedBlocks: PublicProfileSuggestedBlock[];
  exploreTiles: PublicProfileExploreTile[];
  publicUrl: string;
  analytics: PublicProfileAnalyticsSummary | null;
  projects: ProfileFeaturedProject[];
  serviceOffers: ProfileServiceOffer[];
  // Mirrors backend live-page visibility check; false until state is hydrated
  // so the preview banner can warn when the page would 404 publicly.
  isPubliclyVisible: boolean;
}

const ZERO_ANALYTICS_TOTALS: PublicProfileAnalyticsTotals = {
  views: 0,
  profileViews: 0,
  cvViews: 0,
  shareLinkViews: 0,
  uniqueVisitors: 0,
  shares: 0,
  ctaClicks: 0,
  contactClicks: 0,
  downloads: 0,
  blockClicks: 0,
  jobApplyClicks: 0,
};

function zeroAnalyticsBreakdown(): PublicProfileAnalyticsBreakdown {
  return {
    sources: [],
    locations: [],
    referrers: [],
    surfaces: [],
    cvTargets: [],
    downloadsByFormat: [],
    contacts: [],
    recentActivity: [],
    blockClicks: [],
    ctaTargets: [],
    series: [],
  };
}

function createEmptyAnalytics(
  range: PublicProfileAnalyticsRange = '30d',
): PublicProfileAnalyticsSummary {
  return {
    range,
    // A local placeholder, not a paywalled payload: 'counts_only' would make
    // the insights tab paint locks over an offline/loading state.
    detail: 'full',
    totals: ZERO_ANALYTICS_TOTALS,
    breakdown: zeroAnalyticsBreakdown(),
  };
}

function normalizeText(value?: string | null) {
  return typeof value === 'string' ? value.trim() : '';
}

function createDraftProfile(scope: BuilderScope, mode: BuilderMode): DraftProfile {
  if (mode === 'company' && scope.workspace?.type === 'company') {
    return {
      name: normalizeText(scope.workspace.label),
      bio: normalizeText(
        (scope.workspace as WorkspaceSummary & { description?: string | null }).description,
      ),
      location: normalizeText(
        (scope.workspace as WorkspaceSummary & { location?: string | null }).location,
      ),
      website: normalizeText(
        (scope.workspace as WorkspaceSummary & { website?: string | null }).website,
      ),
      tagline: normalizeText(
        (scope.workspace as WorkspaceSummary & { tagline?: string | null }).tagline,
      ),
    };
  }

  return {
    name: normalizeText(scope.profile?.name) || normalizeText(scope.user?.name),
    bio: normalizeText(scope.profile?.headline),
    location: normalizeText(scope.profile?.location),
    website: normalizeText(scope.profile?.website),
    tagline: '',
  };
}

function cloneAnalytics(summary: PublicProfileAnalyticsSummary | null) {
  return summary
    ? {
        ...summary,
        totals: { ...summary.totals },
        breakdown: {
          ...summary.breakdown,
          sources: (summary.breakdown.sources ?? []).map((source) => ({ ...source })),
          locations: (summary.breakdown.locations ?? []).map((location) => ({ ...location })),
          referrers: (summary.breakdown.referrers ?? []).map((referrer) => ({ ...referrer })),
          surfaces: (summary.breakdown.surfaces ?? []).map((surface) => ({ ...surface })),
          cvTargets: (summary.breakdown.cvTargets ?? []).map((cvTarget) => ({ ...cvTarget })),
          downloadsByFormat: (summary.breakdown.downloadsByFormat ?? []).map((item) => ({ ...item })),
          contacts: (summary.breakdown.contacts ?? []).map((contact) => ({ ...contact })),
          recentActivity: (summary.breakdown.recentActivity ?? []).map((item) => ({ ...item })),
          blockClicks: (summary.breakdown.blockClicks ?? []).map((block) => ({ ...block })),
          ctaTargets: (summary.breakdown.ctaTargets ?? []).map((item) => ({ ...item })),
          series: (summary.breakdown.series ?? []).map((point) => ({ ...point })),
        },
      }
    : null;
}

function updateList<T>(list: T[], nextValue: React.SetStateAction<T[]>) {
  return typeof nextValue === 'function'
    ? (nextValue as (previous: T[]) => T[])(list)
    : nextValue;
}

function serializeSocials(socials: PublicProfileSocial[]) {
  return socials.map((social, index) => ({
    id: social.id,
    url: social.url,
    enabled: social.enabled,
    order:
      typeof social.order === 'number' &&
      Number.isFinite(social.order)
        ? social.order
        : index,
  }));
}

function serializeContacts(contacts: PublicProfileContact[]) {
  return contacts.map((contact, index) => ({
    id: contact.id,
    value: contact.value,
    enabled: contact.enabled,
    order:
      typeof contact.order === 'number' &&
      Number.isFinite(contact.order)
        ? contact.order
        : index,
  }));
}

function serializeTeamMembers(teamMembers: PublicProfileTeamMember[]) {
  return teamMembers.map((member, index) => ({
    id: member.id,
    name: member.name,
    role: member.role,
    bio: member.bio ?? null,
    imageUrl:
      typeof member.imageUrl === 'string' && member.imageUrl.trim().length === 0
        ? null
        : member.imageUrl ?? null,
    linkedinUrl:
      typeof member.linkedinUrl === 'string' && member.linkedinUrl.trim().length === 0
        ? null
        : member.linkedinUrl ?? null,
    websiteUrl:
      typeof member.websiteUrl === 'string' && member.websiteUrl.trim().length === 0
        ? null
        : member.websiteUrl ?? null,
    enabled: member.enabled,
    order:
      typeof member.order === 'number' && Number.isFinite(member.order) ? member.order : index,
  }));
}

function serializeDesign(design: PublicProfileBuilderState['design']) {
  return {
    theme: design.theme,
    accent: design.accent,
    buttonStyle: design.buttonStyle,
    buttonShape: design.buttonShape,
    sharePreviewBackground: design.sharePreviewBackground,
  };
}

function serializeSettings(settings: PublicProfileBuilderState['settings']) {
  return {
    hideBranding: settings.hideBranding,
    sensitiveContent: settings.sensitiveContent,
    showContactChannelsPublicly: settings.showContactChannelsPublicly,
    hasSharedTabbio: settings.hasSharedTabbio,
  };
}

function areSerializedValuesEqual(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function areBlockWritesEqual(
  previous: PublicProfileBuilderState,
  next: PublicProfileBuilderState,
) {
  return areSerializedValuesEqual(
    serializeBlocks(previous.blocks as PublicProfileBlock[]),
    serializeBlocks(next.blocks as PublicProfileBlock[]),
  );
}

function areSocialWritesEqual(
  previous: PublicProfileBuilderState,
  next: PublicProfileBuilderState,
) {
  return areSerializedValuesEqual(
    serializeSocials(previous.socials),
    serializeSocials(next.socials),
  );
}

function areContactWritesEqual(
  previous: PublicProfileBuilderState,
  next: PublicProfileBuilderState,
) {
  return areSerializedValuesEqual(
    serializeContacts(previous.contacts),
    serializeContacts(next.contacts),
  );
}

function areTeamWritesEqual(
  previous: PublicProfileBuilderState,
  next: PublicProfileBuilderState,
) {
  return areSerializedValuesEqual(
    serializeTeamMembers(previous.teamMembers),
    serializeTeamMembers(next.teamMembers),
  );
}

function areDesignWritesEqual(
  previous: PublicProfileBuilderState,
  next: PublicProfileBuilderState,
) {
  return areSerializedValuesEqual(
    serializeDesign(previous.design),
    serializeDesign(next.design),
  );
}

function areSettingsWritesEqual(
  previous: PublicProfileBuilderState,
  next: PublicProfileBuilderState,
) {
  return areSerializedValuesEqual(
    serializeSettings(previous.settings),
    serializeSettings(next.settings),
  );
}

/**
 * Autosave writes are debounced per slice, so a pending edit to one form no
 * longer cancels a pending edit to another (they used to share one timer, which
 * silently dropped the earlier form's write).
 */
type BuilderWriteKey = 'blocks' | 'socials' | 'contacts' | 'team' | 'design' | 'settings';

/**
 * One autosaved write: which slice it owns, how to send it, and how to tell
 * whether the edit changed anything the server cares about.
 */
type BuilderWrite = {
  key: BuilderWriteKey;
  persist: (next: PublicProfileBuilderState) => Promise<PublicProfileBuilderState>;
  isEqual?: (
    previous: PublicProfileBuilderState,
    next: PublicProfileBuilderState,
  ) => boolean;
};

/** The single builder-state slice each write owns — used to roll back just that slice on failure. */
const BUILDER_WRITE_SLICE: Record<BuilderWriteKey, keyof PublicProfileBuilderState> = {
  blocks: 'blocks',
  socials: 'socials',
  contacts: 'contacts',
  team: 'teamMembers',
  design: 'design',
  settings: 'settings',
};

/**
 * The API keeps a link it cannot canonicalize but forces `enabled: false` on it
 * (`normalizeLinkEntry`), so the public page renders no broken CTA. That
 * verdict is the server's to make, so it is carried back even though the row's
 * text stays client-owned — but only from the owning slice's newest response,
 * which is the only one fresh enough to be talking about these rows.
 */
function applyServerRowDisables<T extends { id: string; enabled: boolean }>(
  latest: T[],
  saved: T[],
): T[] {
  const savedById = new Map(saved.map((row) => [row.id, row]));
  return latest.map((row) => {
    const server = savedById.get(row.id);
    return server && row.enabled && !server.enabled ? { ...row, enabled: false } : row;
  });
}

/**
 * Reconciles an autosave response with the freshest client state.
 *
 * Every builder PATCH re-reads and returns the whole builder state, and the API
 * trims and sanitizes text on the way in (`z.string().trim()`). Spreading that
 * response over the cache — which every builder form renders from — stripped
 * whitespace the user had just typed, reverted keystrokes made while the
 * request was in flight, and (now that slices save concurrently) let one form's
 * response overwrite another form's newer values.
 *
 * So a write response only refreshes what the server owns: page id, urls,
 * owner, analytics, checklist, visibility. Every slice the user edits stays
 * client-owned — including block/team list membership and order, which a
 * response for another slice always reports staler than the cache — until the
 * next builder GET reconciles against server truth.
 */
function mergeSavedBuilderState(
  key: BuilderWriteKey,
  saved: PublicProfileBuilderState,
  latest: PublicProfileBuilderState,
): PublicProfileBuilderState {
  return {
    ...saved,
    design: latest.design,
    settings: latest.settings,
    blocks: latest.blocks,
    socials:
      key === 'socials'
        ? applyServerRowDisables(latest.socials, saved.socials)
        : latest.socials,
    contacts:
      key === 'contacts'
        ? applyServerRowDisables(latest.contacts, saved.contacts)
        : latest.contacts,
    teamMembers: latest.teamMembers,
  };
}

/** The most recent still-unresolved write failure, or null once all are retired. */
function latestFailure(failures: Map<string, string>): string | null {
  let message: string | null = null;
  failures.forEach((value) => {
    message = value;
  });
  return message;
}

function makeModeKey(mode: BuilderMode, workspace?: WorkspaceSummary) {
  return mode === 'company' ? workspace?.id ?? null : null;
}

function buildIdentitySignature(
  mode: BuilderMode,
  profile: ProfileResponse | undefined,
  user: BuilderUser | null | undefined,
  workspace?: WorkspaceSummary,
) {
  if (mode === 'company' && workspace?.type === 'company') {
    const companyWorkspace = workspace as WorkspaceSummary & {
      description?: string | null;
      location?: string | null;
      logo?: string | null;
      website?: string | null;
    };

    return JSON.stringify([
      companyWorkspace.id,
      companyWorkspace.slug,
      companyWorkspace.label,
      companyWorkspace.description ?? '',
      companyWorkspace.location ?? '',
      companyWorkspace.logo ?? '',
      companyWorkspace.website ?? '',
    ]);
  }

  return JSON.stringify([
    user?.id ?? '',
    user?.username ?? '',
    user?.name ?? '',
    user?.image ?? '',
    profile?.name ?? '',
    profile?.headline ?? '',
    profile?.location ?? '',
    profile?.avatarUrl ?? '',
    profile?.website ?? '',
    profile?.linkedin ?? '',
    profile?.contactEmail ?? '',
    profile?.phone ?? '',
  ]);
}

export function usePublicProfileBuilder({
  profile,
  user,
  workspaceType,
  workspace,
}: {
  profile: ProfileResponse | undefined;
  user: BuilderUser | null | undefined;
  workspaceType?: 'personal' | 'company';
  workspace?: WorkspaceSummary;
}): PublicProfileBuilderModel {
  const queryClient = useQueryClient();
  const routeMode: BuilderMode = workspaceType === 'company' ? 'company' : 'individual';
  const companyWorkspace = workspace?.type === 'company' ? workspace : undefined;
  const canLoadCurrentMode =
    routeMode === 'company' ? Boolean(companyWorkspace?.id) : Boolean(user?.id);
  const scopeKey = makeModeKey(routeMode, companyWorkspace);
  const queryKey = QUERY_KEYS.PUBLIC_PROFILE_BUILDER(routeMode, scopeKey);
  // Identifies the page being edited — the individual page, or one specific
  // company workspace. Drafts and in-flight writes are tracked per scope so
  // switching workspaces never carries one page's state onto another.
  const scopeId = `${routeMode}:${scopeKey ?? 'self'}`;

  const [isPreview, setIsPreview] = useState(false);
  // Only the fields the user actually edited are held here — a field absent
  // from the patch is "not edited", which is what lets an edit to `''` mean
  // "cleared" rather than "unset".
  const [draftOverrides, setDraftOverrides] = useState<Record<string, Partial<DraftProfile>>>(
    {},
  );
  const [designOverrides, setDesignOverrides] = useState<
    Record<BuilderMode, ScopedBuilderOverrides['designOverrides']>
  >({
    individual: { accent: false, buttonShape: false, buttonStyle: false },
    company: { accent: false, buttonShape: false, buttonStyle: false },
  });
  const [pendingWriteCount, setPendingWriteCount] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const identitySignatureRef = useRef<string | null>(null);

  const builderQuery = useQuery<PublicProfileBuilderState>({
    queryKey,
    queryFn: () => fetchPublicProfileBuilderState({ mode: routeMode, range: '30d' }),
    enabled: canLoadCurrentMode,
    staleTime: 15_000,
  });
  const refetchBuilder = builderQuery.refetch;

  const builderState = builderQuery.data ?? null;
  const isHydrated = Boolean(builderState);
  const isLoading =
    !isHydrated && (!canLoadCurrentMode || builderQuery.isPending || builderQuery.isFetching);
  const loadError = !isHydrated && builderQuery.error
    ? builderQuery.error instanceof Error
      ? builderQuery.error.message
      : 'Failed to load public profile builder'
    : null;

  const baseDraft = useMemo(
    () => createDraftProfile({ profile, user, workspace }, routeMode),
    [profile, routeMode, user, workspace],
  );
  const profileDraft = draftOverrides[scopeId] ?? NO_DRAFT_OVERRIDES;
  const draftProfile = useMemo(() => {
    const owner = builderState?.owner;
    // An edited field wins outright (`??`), including when it was cleared.
    // `||` read an emptied field as "unset", so a cleared field reverted on
    // screen for as long as the workspace/profile prop still carried the old
    // value — which it does until that query refreshes, i.e. right after a save.
    return {
      name: profileDraft.name ?? (owner?.name || baseDraft.name),
      bio:
        profileDraft.bio ??
        (routeMode === 'company'
          ? owner?.bio || baseDraft.bio
          : owner?.bio || owner?.headline || baseDraft.bio),
      location: profileDraft.location ?? (owner?.location || baseDraft.location),
      website: profileDraft.website ?? (owner?.website || baseDraft.website),
      // Company mode: the server maps Company.tagline onto owner.headline.
      tagline:
        routeMode === 'company'
          ? profileDraft.tagline ?? (owner?.headline || baseDraft.tagline)
          : '',
    };
  }, [
    baseDraft.bio,
    baseDraft.location,
    baseDraft.name,
    baseDraft.tagline,
    baseDraft.website,
    builderState?.owner,
    profileDraft,
    routeMode,
  ]);

  const username = useMemo(() => {
    if (builderState?.owner.kind === 'company') {
      return builderState.owner.slug || 'company';
    }
    if (builderState?.owner.kind === 'user') {
      return builderState.owner.username || 'user';
    }
    if (routeMode === 'company' && companyWorkspace) {
      return normalizeText(companyWorkspace.slug) || 'company';
    }
    return normalizeText(profile?.username) || normalizeText(user?.username) || 'user';
  }, [builderState?.owner, companyWorkspace, profile?.username, routeMode, user?.username]);

  const publicPrivacy = useMemo(() => {
    if (routeMode === 'company') {
      return DEFAULT_PUBLIC_PROFILE_PRIVACY;
    }
    return getPublicProfileEffectivePrivacy(
      normalizeProfilePrivacySettings(profile?.privacy),
    );
  }, [profile?.privacy, routeMode]);

  const avatarUrl = useMemo(() => {
    const rawAvatarUrl = (() => {
      if (builderState?.owner.avatarUrl) {
        return builderState.owner.avatarUrl;
      }
      if (routeMode === 'company' && companyWorkspace?.type === 'company') {
        return normalizeText(
          (companyWorkspace as WorkspaceSummary & { logo?: string | null }).logo,
        );
      }
      return profile?.avatarUrl || '';
    })();

    if (routeMode === 'individual' && !publicPrivacy.showPhoto) {
      return '';
    }
    return rawAvatarUrl;
  }, [
    builderState?.owner.avatarUrl,
    companyWorkspace,
    profile?.avatarUrl,
    publicPrivacy.showPhoto,
    routeMode,
    user?.image,
  ]);

  const pageId = builderState?.pageId ?? null;
  const blocks = useMemo(
    () => clonePublicProfileBlocks((builderState?.blocks as PublicProfileBlock[] | undefined) ?? []),
    [builderState?.blocks],
  );
  const socials = useMemo(() => builderState?.socials ?? [], [builderState?.socials]);
  const contacts = useMemo(() => builderState?.contacts ?? [], [builderState?.contacts]);
  const teamMembers = useMemo(() => builderState?.teamMembers ?? [], [builderState?.teamMembers]);
  const analytics = useMemo(() => cloneAnalytics(builderState?.analytics ?? null), [builderState?.analytics]);

  const publicUrl = useMemo(() => {
    if (builderState?.publicUrl) {
      return builderState.publicUrl;
    }
    return buildPublicProfileUrl(username, routeMode) ?? '';
  }, [builderState?.publicUrl, routeMode, username]);

  const currentTheme = useMemo(
    () => getPublicProfileTheme((builderState?.design.theme as PublicProfileTheme['id']) ?? 'air'),
    [builderState?.design.theme],
  );

  const projects = useMemo(() => {
    const items = profile?.projects ?? [];
    return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [profile?.projects]);

  const serviceOffers = useMemo(() => {
    const items = profile?.serviceOffers ?? [];
    return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [profile?.serviceOffers]);

  // Writes are tracked per scope *and* slice, so switching workspaces never
  // cancels or supersedes a write that belongs to the page you left.
  const persistTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>> | undefined>(
    undefined,
  );
  // Per-write counter: a response may only touch the cache while it is still
  // the newest write scheduled for its slice. Slices save concurrently now, so
  // two writes for the same form can be in flight at once and settle out of
  // order.
  const writeSeqRef = useRef<Map<string, number> | undefined>(undefined);
  const failedWritesRef = useRef<Map<string, string> | undefined>(undefined);
  const persistState = useCallback(
    (
      key: BuilderWriteKey,
      next: PublicProfileBuilderState,
      persist: (state: PublicProfileBuilderState) => Promise<PublicProfileBuilderState>,
      previous: PublicProfileBuilderState,
    ) => {
      const writeId = `${scopeId}|${key}`;
      const timers = (persistTimersRef.current ??= new Map());
      const sequences = (writeSeqRef.current ??= new Map());
      const failures = (failedWritesRef.current ??= new Map());

      // Retiring this write's own failure must not silence another slice's —
      // that edit really was rolled back and nothing here says otherwise.
      if (failures.delete(writeId)) {
        setSaveError(latestFailure(failures));
      }

      const seq = (sequences.get(writeId) ?? 0) + 1;
      sequences.set(writeId, seq);

      const pending = timers.get(writeId);
      if (pending) {
        clearTimeout(pending);
      } else {
        setPendingWriteCount((count) => count + 1);
      }

      timers.set(
        writeId,
        setTimeout(async () => {
          timers.delete(writeId);
          try {
            const saved = await persist(next);
            if (sequences.get(writeId) !== seq) {
              // Superseded while in flight: the newer write owns this slice.
              return;
            }
            // Merge against the freshest cache entry, not the `next` snapshot
            // captured when the debounce fired — the user may have kept typing
            // (here or in another form) while this request was in flight, and
            // that cache value is newer than anything the response carries.
            const latest = queryClient.getQueryData<PublicProfileBuilderState>(queryKey) ?? next;
            queryClient.setQueryData(queryKey, mergeSavedBuilderState(key, saved, latest));
          } catch (error) {
            if (sequences.get(writeId) !== seq) {
              // A newer write for this slice is already queued: leave the cache
              // to it rather than rolling its edit back, and let it report.
              return;
            }
            // Roll back only the slice this write owned — other forms may have
            // been edited (or saved) since `previous` was captured.
            const slice = BUILDER_WRITE_SLICE[key];
            queryClient.setQueryData<PublicProfileBuilderState>(queryKey, (current) =>
              current ? { ...current, [slice]: previous[slice] } : previous,
            );
            // Re-insert so the banner reports the newest failure, not the
            // oldest one that happens to have failed before.
            failures.delete(writeId);
            failures.set(
              writeId,
              error instanceof Error ? error.message : 'Failed to save changes',
            );
            setSaveError(latestFailure(failures));
          } finally {
            setPendingWriteCount((count) => Math.max(0, count - 1));
          }
        }, 700),
      );
    },
    [queryClient, queryKey, scopeId],
  );

  useEffect(() => {
    return () => {
      persistTimersRef.current?.forEach((timer) => clearTimeout(timer));
      persistTimersRef.current?.clear();
    };
  }, []);

  const updateCurrentState = useCallback(
    (
      updater: (current: PublicProfileBuilderState) => PublicProfileBuilderState,
      write?: BuilderWrite,
    ) => {
      if (!builderState) {
        return;
      }

      const previous = queryClient.getQueryData<PublicProfileBuilderState>(queryKey) ?? builderState;
      const next = updater(previous);
      const isEqual = write?.isEqual ?? ((a: PublicProfileBuilderState, b: PublicProfileBuilderState) => a === b);
      if (next === previous || isEqual(previous, next)) {
        return;
      }
      queryClient.setQueryData(queryKey, next);

      if (!write || !canLoadCurrentMode) {
        return;
      }

      void persistState(write.key, next, write.persist, previous);
    },
    [builderState, canLoadCurrentMode, persistState, queryClient, queryKey],
  );

  useEffect(() => {
    const nextSignature = buildIdentitySignature(routeMode, profile, user, workspace);

    if (!builderState) {
      identitySignatureRef.current = nextSignature;
      return;
    }

    if (identitySignatureRef.current && identitySignatureRef.current !== nextSignature) {
      void refetchBuilder();
    }

    identitySignatureRef.current = nextSignature;
  }, [builderState, profile, refetchBuilder, routeMode, user, workspace]);

  const setDraftProfile = useCallback(
    (patch: Partial<DraftProfile>) => {
      setDraftOverrides((previous) => ({
        ...previous,
        [scopeId]: {
          ...(previous[scopeId] ?? {}),
          ...patch,
        },
      }));

      updateCurrentState((current) => ({
        ...current,
        owner: {
          ...current.owner,
          name: patch.name ?? current.owner.name,
          bio: patch.bio ?? current.owner.bio ?? null,
          // Individual mode couples bio and headline (they are one field);
          // company mode keeps headline as the distinct tagline (PRD §6.1).
          headline:
            routeMode === 'company'
              ? patch.tagline ?? current.owner.headline ?? null
              : patch.bio ?? current.owner.headline ?? null,
          location: patch.location ?? current.owner.location ?? null,
          website: patch.website ?? current.owner.website ?? null,
        },
      }));
    },
    [routeMode, scopeId, updateCurrentState],
  );

  const setBlocks = useCallback<React.Dispatch<React.SetStateAction<PublicProfileBlock[]>>>(
    (nextValue) => {
      updateCurrentState(
        (current) => ({
          ...current,
          blocks: serializeBlocks(
            updateList(
              current.blocks as PublicProfileBlock[],
              nextValue as React.SetStateAction<PublicProfileBlock[]>,
            ),
          ),
        }),
        {
          key: 'blocks',
          persist: (next) =>
            updatePublicProfileBlocks({
              mode: routeMode,
              blocks: serializeBlocks(next.blocks) as PublicProfileBlock[],
            }),
          isEqual: areBlockWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const toggleBlockEnabled = useCallback(
    (id: string) => {
      setBlocks((previous) =>
        previous.map((block) => (block.id === id ? { ...block, enabled: !block.enabled } : block)),
      );
    },
    [setBlocks],
  );

  const reorderBlocksByIndices = useCallback(
    (fromIndex: number, toIndex: number) => {
      setBlocks((previous) => {
        if (
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= previous.length ||
          toIndex >= previous.length
        ) {
          return previous;
        }

        const next = [...previous];
        const [picked] = next.splice(fromIndex, 1);
        if (!picked) {
          return previous;
        }
        next.splice(toIndex, 0, picked);
        return next.map((block, index) => ({ ...block, order: index }));
      });
    },
    [setBlocks],
  );

  const reorderBlock = useCallback(
    (id: string, direction: 'up' | 'down') => {
      setBlocks((previous) => {
        const currentIndex = previous.findIndex((block) => block.id === id);
        if (currentIndex < 0) return previous;
        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= previous.length) return previous;
        const next = [...previous];
        const [picked] = next.splice(currentIndex, 1);
        if (!picked) return previous;
        next.splice(targetIndex, 0, picked);
        return next.map((block, index) => ({ ...block, order: index }));
      });
    },
    [setBlocks],
  );

  const addBlock = useCallback(
    (type: PublicProfileBlock['type']) => {
      const existing = blocks.find((block) => block.type === type);
      if (existing) {
        return existing;
      }

      const created = { ...createNewBlock(type), order: 0 };
      setBlocks((previous) => [
        created,
        ...previous.map((block, index) => ({ ...block, order: index + 1 })),
      ]);
      return created;
    },
    [blocks, setBlocks],
  );

  const toggleSocial = useCallback(
    (id: PublicProfileSocial['id']) => {
      updateCurrentState(
        (current) => ({
          ...current,
          socials: current.socials.map((social) =>
            social.id === id ? { ...social, enabled: !social.enabled } : social,
          ),
        }),
        {
          key: 'socials',
          persist: (next) =>
            updatePublicProfileSocials({
              mode: routeMode,
              socials: serializeSocials(next.socials) as PublicProfileSocial[],
            }),
          isEqual: areSocialWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const updateSocialUrl = useCallback(
    (id: PublicProfileSocial['id'], url: string) => {
      updateCurrentState(
        (current) => ({
          ...current,
          socials: current.socials.map((social) =>
            social.id === id ? { ...social, url } : social,
          ),
        }),
        {
          key: 'socials',
          persist: (next) =>
            updatePublicProfileSocials({
              mode: routeMode,
              socials: serializeSocials(next.socials) as PublicProfileSocial[],
            }),
          isEqual: areSocialWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const toggleContact = useCallback(
    (id: PublicProfileContact['id']) => {
      updateCurrentState(
        (current) => ({
          ...current,
          contacts: current.contacts.map((contact) => {
            if (contact.id !== id) return contact;
            // Switching a removable channel off clears its address — that is
            // what "Remove" means. A built-in channel is only being disabled,
            // so it keeps what it holds and comes back unchanged.
            const clears = contact.enabled && !isBuiltInContactChannel(id);
            return {
              ...contact,
              enabled: !contact.enabled,
              value: clears ? '' : contact.value,
            };
          }),
        }),
        {
          key: 'contacts',
          persist: (next) =>
            updatePublicProfileContacts({
              mode: routeMode,
              contacts: serializeContacts(next.contacts) as PublicProfileContact[],
            }),
          isEqual: areContactWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const updateContactValue = useCallback(
    (id: PublicProfileContact['id'], value: string) => {
      updateCurrentState(
        (current) => ({
          ...current,
          contacts: current.contacts.map((contact) =>
            contact.id === id ? { ...contact, value } : contact,
          ),
        }),
        {
          key: 'contacts',
          persist: (next) =>
            updatePublicProfileContacts({
              mode: routeMode,
              contacts: serializeContacts(next.contacts) as PublicProfileContact[],
            }),
          isEqual: areContactWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const setTheme = useCallback(
    (value: PublicProfileTheme['id']) => {
      updateCurrentState(
        (current) => {
          const selectedTheme = getPublicProfileTheme(value);
          return {
            ...current,
            design: {
              ...current.design,
              theme: value,
              accent: designOverrides[routeMode].accent
                ? current.design.accent
                : selectedTheme.defaultAccent,
              buttonStyle: designOverrides[routeMode].buttonStyle
                ? current.design.buttonStyle
                : selectedTheme.defaultButtonStyle,
              buttonShape: designOverrides[routeMode].buttonShape
                ? current.design.buttonShape
                : selectedTheme.defaultButtonShape,
            },
          };
        },
        {
          key: 'design',
          persist: (next) => updatePublicProfileDesign({ mode: routeMode, design: next.design }),
          isEqual: areDesignWritesEqual,
        },
      );
    },
    [designOverrides, routeMode, updateCurrentState],
  );

  const setAccent = useCallback(
    (value: string) => {
      setDesignOverrides((previous) => ({
        ...previous,
        [routeMode]: { ...previous[routeMode], accent: true },
      }));
      updateCurrentState(
        (current) => ({
          ...current,
          design: {
            ...current.design,
            accent: value,
          },
        }),
        {
          key: 'design',
          persist: (next) => updatePublicProfileDesign({ mode: routeMode, design: next.design }),
          isEqual: areDesignWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const setButtonStyle = useCallback(
    (value: PublicProfileButtonStyle) => {
      setDesignOverrides((previous) => ({
        ...previous,
        [routeMode]: { ...previous[routeMode], buttonStyle: true },
      }));
      updateCurrentState(
        (current) => ({
          ...current,
          design: {
            ...current.design,
            buttonStyle: value,
          },
        }),
        {
          key: 'design',
          persist: (next) => updatePublicProfileDesign({ mode: routeMode, design: next.design }),
          isEqual: areDesignWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const setButtonShape = useCallback(
    (value: PublicProfileButtonShape) => {
      setDesignOverrides((previous) => ({
        ...previous,
        [routeMode]: { ...previous[routeMode], buttonShape: true },
      }));
      updateCurrentState(
        (current) => ({
          ...current,
          design: {
            ...current.design,
            buttonShape: value,
          },
        }),
        {
          key: 'design',
          persist: (next) => updatePublicProfileDesign({ mode: routeMode, design: next.design }),
          isEqual: areDesignWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const setSharePreviewBackground = useCallback(
    (value: PublicProfileSharePreviewBackground) => {
      updateCurrentState(
        (current) => ({
          ...current,
          design: {
            ...current.design,
            sharePreviewBackground: value,
          },
        }),
        {
          key: 'design',
          persist: (next) => updatePublicProfileDesign({ mode: routeMode, design: next.design }),
          isEqual: areDesignWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const setHideBranding = useCallback(
    (value: boolean) => {
      updateCurrentState(
        (current) => ({
          ...current,
          settings: {
            ...current.settings,
            hideBranding: value,
          },
        }),
        {
          key: 'settings',
          persist: (next) => updatePublicProfileSettings({ mode: routeMode, settings: next.settings }),
          isEqual: areSettingsWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const setSensitiveContent = useCallback(
    (value: boolean) => {
      updateCurrentState(
        (current) => ({
          ...current,
          settings: {
            ...current.settings,
            sensitiveContent: value,
          },
        }),
        {
          key: 'settings',
          persist: (next) => updatePublicProfileSettings({ mode: routeMode, settings: next.settings }),
          isEqual: areSettingsWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const setShowContactChannelsPublicly = useCallback(
    (value: boolean) => {
      updateCurrentState(
        (current) => ({
          ...current,
          settings: {
            ...current.settings,
            showContactChannelsPublicly: value,
          },
        }),
        {
          key: 'settings',
          persist: (next) => updatePublicProfileSettings({ mode: routeMode, settings: next.settings }),
          isEqual: areSettingsWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const setHasSharedTabbio = useCallback(
    (value: boolean) => {
      updateCurrentState(
        (current) => ({
          ...current,
          settings: {
            ...current.settings,
            hasSharedTabbio: value,
          },
        }),
        {
          key: 'settings',
          persist: (next) => updatePublicProfileSettings({ mode: routeMode, settings: next.settings }),
          isEqual: areSettingsWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const confirmCompanyShare = useCallback(() => {
    if (routeMode !== 'company') {
      return;
    }

    void confirmPublicProfileShare({ mode: 'company' })
      .then((result) => {
        queryClient.setQueryData<BuilderStateWithChecklist>(queryKey, (previous) =>
          previous ? { ...previous, checklist: result.checklist } : previous,
        );
      })
      .catch(() => {
        // The share action itself already succeeded; share-confirm is server
        // bookkeeping (and may 404 until the backend lands) — stay silent.
      });
  }, [queryClient, queryKey, routeMode]);

  const setTeamMembers = useCallback<React.Dispatch<React.SetStateAction<PublicProfileTeamMember[]>>>(
    (nextValue) => {
      if (routeMode !== 'company') {
        return;
      }

      updateCurrentState(
        (current) => ({
          ...current,
          teamMembers: serializeTeamMembers(updateList(current.teamMembers ?? [], nextValue)),
        }),
        {
          key: 'team',
          persist: (next) =>
            updatePublicProfileTeam({
              mode: 'company',
              teamMembers: serializeTeamMembers(next.teamMembers) as PublicProfileTeamMember[],
            }),
          isEqual: areTeamWritesEqual,
        },
      );
    },
    [routeMode, updateCurrentState],
  );

  const activeSocials = useMemo(
    () => socials.filter((social) => social.enabled),
    [socials],
  );
  const activeContacts = useMemo(
    () => contacts.filter((contact) => contact.enabled),
    [contacts],
  );

  const checklistItems = useMemo<PublicProfileChecklistItem[]>(() => {
    if (routeMode === 'company') {
      // Company checklist is server truth (PRD §11): derived from persisted
      // state on the builder GET. While the payload is absent every item reads
      // incomplete — the ring only ever shows real persisted progress.
      const serverChecklist =
        (builderState as BuilderStateWithChecklist | null)?.checklist ?? null;
      return mapCompanyChecklistToItems(serverChecklist);
    }

    const currentDesign = builderState?.design ?? {
      theme: currentTheme.id,
      accent: currentTheme.defaultAccent,
      buttonStyle: currentTheme.defaultButtonStyle,
      buttonShape: currentTheme.defaultButtonShape,
      sharePreviewBackground: 'gradient' as PublicProfileSharePreviewBackground,
    };
    const currentSettings = builderState?.settings ?? {
      hideBranding: false,
      sensitiveContent: false,
      showContactChannelsPublicly: false,
      hasSharedTabbio: false,
    };

    const hasNameBio = Boolean(draftProfile.name.trim() && draftProfile.bio.trim());
    const hasImage = Boolean(avatarUrl);
    const hasSocials = activeSocials.filter((social) => social.url.trim().length > 0).length >= 2;
    const hasLinks = blocks.filter((block) => block.enabled).length >= 4;
    const hasDesign =
      currentDesign.theme !== 'air' ||
      currentDesign.accent !== PUBLIC_PROFILE_ACCENTS[0] ||
      currentDesign.buttonStyle !== 'fill' ||
      currentDesign.buttonShape !== 'rounded';

    const map: Record<PublicProfileChecklistId, boolean> = {
      name_bio: hasNameBio,
      profile_image: hasImage,
      socials: hasSocials,
      important_links: hasLinks,
      customize_design: hasDesign,
      share_tabbio: currentSettings.hasSharedTabbio,
    };

    return PUBLIC_PROFILE_CHECKLIST_ORDER.map((id) => ({
      id,
      completed: map[id],
    }));
  }, [activeSocials, avatarUrl, blocks, builderState, currentTheme, draftProfile, routeMode]);

  const checklistDone = checklistItems.filter((item) => item.completed).length;
  const checklistTotal = checklistItems.length;
  const completionPercent = Math.round((checklistDone / Math.max(checklistTotal, 1)) * 100);

  // Company Phase 1 has no suggested/explore catalogue: the Add sheet offers
  // PUBLIC_PROFILE_ADD_BASICS_COMPANY plus smart add (PRD §6.5).
  const suggestedBlocks =
    routeMode === 'company'
      ? NO_SUGGESTED_BLOCKS
      : PUBLIC_PROFILE_SUGGESTED_BLOCKS_INDIVIDUAL;

  const exploreTiles =
    routeMode === 'company'
      ? NO_EXPLORE_TILES
      : PUBLIC_PROFILE_EXPLORE_TILES_INDIVIDUAL;

  return {
    pageId,
    isHydrated,
    isLoading,
    loadError,
    isSaving: pendingWriteCount > 0,
    saveError,
    retry: () => builderQuery.refetch(),
    clearSaveError: () => {
      failedWritesRef.current?.clear();
      setSaveError(null);
    },
    isPreview,
    setIsPreview,
    profileMode: routeMode,
    setProfileMode: () => undefined,
    routeMode,
    draftProfile,
    setDraftProfile,
    username,
    avatarUrl,
    publicPrivacy,
    blocks,
    setBlocks,
    toggleBlockEnabled,
    reorderBlock,
    reorderBlocksByIndices,
    addBlock,
    socials,
    activeSocials,
    toggleSocial,
    updateSocialUrl,
    contacts,
    activeContacts,
    toggleContact,
    updateContactValue,
    theme: (builderState?.design.theme as PublicProfileTheme['id']) ?? currentTheme.id,
    setTheme,
    accent: builderState?.design.accent ?? currentTheme.defaultAccent,
    setAccent,
    buttonStyle: builderState?.design.buttonStyle ?? currentTheme.defaultButtonStyle,
    setButtonStyle,
    buttonShape: builderState?.design.buttonShape ?? currentTheme.defaultButtonShape,
    setButtonShape,
    sharePreviewBackground:
      builderState?.design.sharePreviewBackground ?? 'gradient',
    setSharePreviewBackground,
    hideBranding: builderState?.settings.hideBranding ?? false,
    setHideBranding,
    sensitiveContent: builderState?.settings.sensitiveContent ?? false,
    setSensitiveContent,
    showContactChannelsPublicly:
      builderState?.settings.showContactChannelsPublicly ?? false,
    setShowContactChannelsPublicly,
    hasSharedTabbio: builderState?.settings.hasSharedTabbio ?? false,
    setHasSharedTabbio,
    confirmCompanyShare,
    teamMembers,
    setTeamMembers,
    checklistItems,
    checklistDone,
    checklistTotal,
    completionPercent,
    suggestedBlocks,
    exploreTiles,
    publicUrl,
    analytics: analytics ?? (isHydrated ? createEmptyAnalytics() : null),
    projects,
    serviceOffers,
    isPubliclyVisible: builderState?.isPubliclyVisible ?? false,
  };
}
